import { readableNumber } from '@attain-apps/string-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as common from '../../../../../common'
import { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { Concatenate } from './concatenate'
import { Multiply } from './multiply'

const COMMAS_DEFAULT = true
const MIN_DECIMALS_DEFAULT = 0
const MAX_DECIMALS_DEFAULT = 7

// We need to use `lazy` to avoid circular reference issue
const isExpression = TypeChecker.Factory.lazy(() => Expression.isExpression)
// NOTE: unfortunately, TypeChecker<Expression<any>> is not assignable to
// every TypeChecker<Expression<T>>
const isStringExpression = isExpression satisfies TypeChecker<
  Expression<any>
> as TypeChecker<Expression<string>>
const isNumberExpression = isExpression satisfies TypeChecker<
  Expression<any>
> as TypeChecker<Expression<number>>
const isBooleanExpression = isExpression satisfies TypeChecker<
  Expression<any>
> as TypeChecker<Expression<boolean>>

/**
 * TODO:
 * - without options, uses default based on data type of expression
 * - with custom style, it merges with default based on data type of expression
 */
export interface FormatNumber<out T = any> {
  /** {@link FormatNumber} */
  readonly [Key.FormatNumber]: T extends string
    ? Expression<number> | readonly [Expression<number>, FormatNumber.Style?]
    : never
}
export namespace FormatNumber {
  export const isFormatNumber: TypeChecker<FormatNumber> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<FormatNumber>({
        [Key.FormatNumber]: TypeChecker.Factory.union(
          isNumberExpression,
          TypeChecker.Factory.tuple(
            isNumberExpression,
            TypeChecker.Factory.optional(FormatNumber.Style.isStyle),
          ),
        ),
      }),
    ).usingAlias('Fn.FormatNumber')
  export const assertIsFormatNumber: TypeAsserter<FormatNumber> =
    TypeAsserter.Factory.from(isFormatNumber)

  export type Style = Style.Preset | Style.Custom
  export namespace Style {
    export const isStyle: TypeChecker<Style> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.union(Preset.isPreset, Custom.isCustom),
    ).usingAlias('Fn.Style')
    export const assertIsStyle: TypeAsserter<Style> =
      TypeAsserter.Factory.from(isStyle)

    /**
     * These represent standard {@link Style} of numbers
     */
    export type Preset = readableNumber.Preset
    export namespace Preset {
      export const isPreset: TypeChecker<Preset> = TypeChecker.Factory.custom(
        readableNumber.Preset.isPreset,
        {
          typeDescription: 'readableNumber.Preset',
        },
      )

      /**
       * Returns a {@link Style.Custom} for the given {@link Style.Preset}
       */
      export function toCustom(preset: Preset): Custom {
        const { noCommas, decimals, prefix, scalar, suffix } =
          readableNumber.Preset.toOptions(preset)

        return {
          ',': !noCommas,
          '.': TypeChecker.isNumber(decimals)
            ? { always: decimals }
            : {
                max: decimals?.max,
                min: decimals?.min,
              },
          prefix,
          scalar,
          suffix,
        }
      }
    }

    export interface Custom {
      /**
       * Number is multipled by this scalar before being formatted
       *
       * @default 1
       * @example
       * // scalar: 1
       * 1 => '1'
       * 0.5 => '0.5'
       * 2 => '2'
       * // scalar: 2
       * 1 => '2'
       * 0.5 => '1'
       * 2 => '4'
       * // scalar: 100
       * 1 => '100'
       * 0.5 => '50'
       * 2 => '200'
       */
      readonly scalar?: Expression<number>
      /**
       * This text is placed
       */
      readonly prefix?: Expression<string>
      readonly suffix?: Expression<string>
      /**
       * Places commas as separators at every third order of magnitude
       *
       * @default true
       * @example
       * // commas: true
       * 123 => '123'
       * 1234 => '1,234'
       * 123456 => '123,456'
       * 1234567890 => '1,234,567,890'
       * // commas: false
       * 123 => '123'
       * 1234 => '1234'
       * 123456 => '123456'
       * 1234567890 => '1234567890'
       */
      readonly ','?: Expression<boolean>
      /**
       * Determines how many decimals are displayed
       *
       * @default no min or max
       * @example
       * // decimals: 2
       * 123 => '123.00'
       * 123.4 => '123.40'
       * 123.45 => '123.45'
       * 123.456 => '123.46'
       * 123.454 => '123.45'
       * // decimals: { min: 1 }
       * 123 => '123.0'
       * 123.4 => '123.4'
       * 123.456 => '123.456'
       * 123.456789 => '123.456789'
       * // decimals: { max: 3 }
       * 123 => '123'
       * 123.4 => '123.4'
       * 123.456 => '123.456'
       * 123.456789 => '123.457'
       * // decimals: { max: 4, min: 2 }
       * 123 => '123.00'
       * 123.4 => '123.40'
       * 123.456 => '123.456'
       * 123.456789 => '123.4568'
       */
      readonly '.'?: MutuallyExclusive<
        [
          {
            readonly always: Expression<number>
          },
          {
            readonly min?: Expression<number>
            readonly max?: Expression<number>
          },
        ]
      >
    }
    export namespace Custom {
      export const isCustom: TypeChecker<Custom> = TypeChecker.Factory.lazy(
        () =>
          TypeChecker.Factory.object<Custom>({
            ',': TypeChecker.Factory.optional(isBooleanExpression),
            '.': TypeChecker.Factory.optional(
              TypeChecker.Factory.union(
                TypeChecker.Factory.object({ always: isNumberExpression }),
                TypeChecker.Factory.object({
                  max: TypeChecker.Factory.optional(isNumberExpression),
                  min: TypeChecker.Factory.optional(isNumberExpression),
                }),
              ),
            ),
            prefix: TypeChecker.Factory.optional(isStringExpression),
            scalar: TypeChecker.Factory.optional(isNumberExpression),
            suffix: TypeChecker.Factory.optional(isStringExpression),
          }),
      )

      /**
       * Normalizes a {@link Style} into a {@link Style.Custom} to make it
       * easier to process
       */
      export function from(style: Readonly<Style>): Custom {
        return Preset.isPreset(style) ? Preset.toCustom(style) : style
      }
    }
  }

  /**
   * @see {@link Node}
   */
  export class Node<T extends string = string> extends fnNode.Node<
    T,
    Key.FormatNumber,
    {
      readonly number: expressionNode.Node<number>
      readonly commas: expressionNode.Node<boolean>
      readonly minDecimals: expressionNode.Node<number>
      readonly maxDecimals: expressionNode.Node<number>
    }
  > {
    public readonly type = common.value.types.Intrinsic.Text

    constructor(arg: Node<T>['arg']) {
      super(Key.FormatNumber, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  const expressionsInArgs: FnHelper<FormatNumber>['expressionsInArgs'] = fn => {
    const arg = fn[Key.FormatNumber]
    const expression = TypeChecker.isReadonlyArray(arg) ? arg[0] : arg
    return [expression]
  }
  export const HELPER: FnHelper<FormatNumber> = {
    createNode: (fn, context) => {
      /* -------------------------- Extracting Arguments -------------------------- */
      const arg = fn[Key.FormatNumber]
      let numberNode: expressionNode.Node<number>
      let style: Style.Custom
      if (TypeChecker.isReadonlyArray(arg)) {
        const [numberExpression, rawStyle] = arg
        numberNode = expressionNode.from(numberExpression, context)
        // If the rawStyle is a preset, we use that preset. Otherwise, we merge
        // the inferred preset with whatever custom options are in the rawStyle,
        // if any
        style = Style.Preset.isPreset(rawStyle)
          ? Style.Preset.toCustom(rawStyle)
          : {
              ...Style.Custom.from(
                common.value.types.Intrinsic.ForNumber.toReadableNumberPreset(
                  TypeAsserter.validateButJustWarn(
                    numberNode.type,
                    common.value.types.Intrinsic.ForNumber.assertIsForNumber,
                  ),
                ),
              ),
              ...rawStyle,
            }
      } else {
        numberNode = expressionNode.from(arg, context)
        style = Style.Custom.from(
          common.value.types.Intrinsic.ForNumber.toReadableNumberPreset(
            TypeAsserter.validateButJustWarn(
              numberNode.type,
              common.value.types.Intrinsic.ForNumber.assertIsForNumber,
            ),
          ),
        )
      }

      /* --------------------- Creating Style Expression Nodes -------------------- */
      const {
        ',': commas,
        '.': decimals,
        prefix,
        scalar,
        suffix,
      } = Style.Custom.from(style)
      const commasNode =
        commas === undefined
          ? new expressionNode.ForValue(COMMAS_DEFAULT)
          : expressionNode.from(commas, context)
      const prefixNode =
        prefix === undefined ? undefined : expressionNode.from(prefix, context)
      const suffixNode =
        suffix === undefined ? undefined : expressionNode.from(suffix, context)
      const scalarNode =
        scalar === undefined ? undefined : expressionNode.from(scalar, context)
      let minDecimalsNode: expressionNode.Node<number> =
        new expressionNode.ForValue(MIN_DECIMALS_DEFAULT)
      let maxDecimalsNode: expressionNode.Node<number> =
        new expressionNode.ForValue(MAX_DECIMALS_DEFAULT)
      if (decimals !== undefined) {
        const { always, max, min } = decimals
        if (always !== undefined) {
          minDecimalsNode = maxDecimalsNode = expressionNode.from(
            always,
            context,
          )
        } else {
          if (min !== undefined) {
            minDecimalsNode = expressionNode.from(min, context)
          }
          if (max !== undefined) {
            maxDecimalsNode = expressionNode.from(max, context)
          }
        }
      }

      /* ----------------------------- Applying Scalar ---------------------------- */
      /**
       * This is the result of multiplying the {@link numberNode} by the
       * {@link scalarNode}
       */
      const finalNumberNode =
        scalarNode === undefined
          ? // No scalar, means the given number is the final number
            numberNode
          : expressionNode.ForValue.isForValue(numberNode) &&
              expressionNode.ForValue.isForValue(scalarNode)
            ? // If both the scalar and given number are values, we can just
              // compute the final value
              new expressionNode.ForValue(
                Number(numberNode.value) * Number(scalarNode.value),
              )
            : // Otherwise, we must create a multiply expression to do the
              // multiplication
              new Multiply.Node([numberNode, scalarNode])

      /* ----------------------- Applying Commas & Decimals ----------------------- */
      let formattedNumberNode: expressionNode.Node<string>
      if (
        expressionNode.ForValue.isForValue(finalNumberNode) &&
        expressionNode.ForValue.isForValue(commasNode) &&
        expressionNode.ForValue.isForValue(minDecimalsNode) &&
        expressionNode.ForValue.isForValue(maxDecimalsNode)
      ) {
        // If the final number and all relevant parameters for formatting are
        // values, we can format the number now
        formattedNumberNode = new expressionNode.ForValue(
          readableNumber(Number(finalNumberNode.value), {
            decimals: {
              max: Number(maxDecimalsNode.value),
              min: Number(minDecimalsNode.value),
            },
            noCommas: !commasNode.value,
          }),
        )
      } else {
        // Otherwise, we need to create an expression node for the formatting
        formattedNumberNode = new Node({
          commas: commasNode,
          maxDecimals: maxDecimalsNode,
          minDecimals: minDecimalsNode,
          number: finalNumberNode,
        })
      }

      /* ------------------------ Applying Prefix & Suffix ------------------------ */
      if (prefixNode === undefined && suffixNode === undefined) {
        // If there is no prefix or suffix, we can just return the formatted
        // number
        return formattedNumberNode
      }
      if (expressionNode.ForValue.isForValue(formattedNumberNode)) {
        // If the formatted number is a value, then we might be able to do the
        // concatenation now
        if (expressionNode.ForValue.isForValue(prefixNode)) {
          if (expressionNode.ForValue.isForValue(suffixNode)) {
            // If the prefix and suffix are both values, then we can do the full
            // concatenation now and end up with a finalized value
            return new expressionNode.ForValue(
              String(prefixNode.value) +
                String(formattedNumberNode.value) +
                String(suffixNode.value),
            )
          }
          // If only the prefix is a value, we can at least concatenate the
          // prefix and formatted number together
          const formattedNumberWithPrefixNode = new expressionNode.ForValue(
            String(prefixNode.value) + String(formattedNumberNode.value),
          )
          return suffixNode === undefined
            ? // If there is no suffix, we can return that concatenated value
              formattedNumberWithPrefixNode
            : // Otherwise, we need to create a new concatenate expression to add
              // the suffix
              new Concatenate.Node(
                [formattedNumberWithPrefixNode, suffixNode],
                common.value.types.Intrinsic.Text,
              )
        } else if (expressionNode.ForValue.isForValue(suffixNode)) {
          // If only the suffix is a value, we can at least concatenate the
          // formatted number and suffix together
          const formattedNumberWithSuffixNode = new expressionNode.ForValue(
            String(formattedNumberNode.value) + String(suffixNode.value),
          )
          return prefixNode === undefined
            ? // If there is no prefix, we can return that concatenated value
              formattedNumberWithSuffixNode
            : // Otherwise, we need to create a new concatenate expression to add
              // the prefix
              new Concatenate.Node(
                [prefixNode, formattedNumberWithSuffixNode],
                common.value.types.Intrinsic.Text,
              )
        }
      }
      // If we were not able to concatenate any values together, and there is at
      // least a prefix or a suffix, we need to create a new concatenate
      // expression to do the full concatenation
      return new Concatenate.Node(
        [prefixNode, formattedNumberNode, suffixNode].filter(
          // Removes prefix or suffix, if one of them doesn't exist
          TypeChecker.isNotNullish.raw,
        ),
        common.value.types.Intrinsic.Text,
      )
    },
    expressionsInArgs,
    typeChecker: isFormatNumber,
  }
}
